// Curated journeys for enei.in — hand-written. Calm, cinematic, sparse.
// Each journey targets one traveller archetype and one state.

export const JOURNEYS = [
    // ───── ADVENTURE ─────
    {
        slug: "dzukou-in-bloom",
        traveller: "adventure",
        state: "nagaland",
        title: "Dzukou, when the lilies wake",
        oneLiner:
            "Two days above the cloud line, where the valley turns lavender and the wind smells of wet pine.",
        duration: "4 nights",
        durationBucket: "short", // short | medium | long
        budgetBand: "30-60k",
        budgetLabel: "₹30,000 – ₹60,000",
        season: "Jun–Aug",
        seasonBucket: "monsoon",
        mood: "Mist · boots · cold mornings",
        image:
            "https://images.unsplash.com/photo-1692652567309-5ac9ae7fe6f7?auto=format&fit=crop&w=1600&q=70",
        intro:
            "Dzukou Valley is not a trail you finish. It opens slowly — first the climb through bamboo, then the ridge, then a green bowl of rolling grass that no photograph quite tells the truth about. In monsoon, the Dzukou lilies bloom only here — pale lavender, slightly bent, as if listening.",
        days: [
            { d: "Day 01", t: "Kohima", b: "Land. Dinner with the Angami family who'll host you in Khonoma — Asia's first green village." },
            { d: "Day 02", t: "Khonoma → Viswema → Dzukou base", b: "Drive 2 hours, then climb 4. Sleep in a stone hut. The sky here has fewer planes than stars." },
            { d: "Day 03", t: "The valley", b: "A slow day. Walk the streams, find the lilies, sit with the silence. No phones. No itinerary." },
            { d: "Day 04", t: "Descend to Kohima", b: "Hot bath, second-flush tea, a long sleep in a real bed." },
            { d: "Day 05", t: "Fly out", b: "Or stay another night. Most people do." },
        ],
        included: ["Local Angami guide", "Stone-hut camping with bedroll & meals", "Permits, all transfers", "Hot tea on every climb"],
    },
    {
        slug: "mechuka-on-a-bike",
        traveller: "adventure",
        state: "arunachal",
        title: "Mechuka, on two wheels",
        oneLiner:
            "Eight days, 1,300 kilometres, a Himalayan valley most Indians can't yet point to on a map.",
        duration: "8 nights",
        durationBucket: "long",
        budgetBand: "60-120k",
        budgetLabel: "₹60,000 – ₹1,20,000",
        season: "Apr–Oct",
        seasonBucket: "summer",
        mood: "Roads · river crossings · prayer flags",
        image:
            "https://images.pexels.com/photos/2815093/pexels-photo-2815093.jpeg?auto=compress&cs=tinysrgb&w=1600&q=70",
        intro:
            "Mechuka sits at the edge of the country — a Memba valley closer to Lhasa than to Delhi in feel. The road in is the journey: Aalo, Likabali, Tato, then a long thin valley with a single river running through it. We ride Royal Enfields. We stop often. We rarely hurry.",
        days: [
            { d: "Day 01", t: "Dibrugarh → Pasighat", b: "Cross the Brahmaputra at Bogibeel. Get used to the bike." },
            { d: "Day 02", t: "Pasighat → Aalo", b: "Switchbacks, cane bridges, a riverside lunch in Boleng." },
            { d: "Day 03", t: "Aalo → Mechuka", b: "The hardest, longest, most beautiful day. Arrive at dusk." },
            { d: "Day 04–05", t: "Mechuka", b: "Samten Yongcha gompa, the gun-shaped river, a Memba homestay with butter tea." },
            { d: "Day 06–07", t: "Return", b: "Different route via Menchukha–Aalo–Pasighat." },
            { d: "Day 08", t: "Dibrugarh", b: "Hand the bikes back. Order a real Assamese thali." },
        ],
        included: ["Royal Enfield Himalayan", "Lead & sweep riders", "ILP permits", "All homestays & meals", "Mechanic backup"],
    },
    {
        slug: "nongriat-deep",
        traveller: "adventure",
        state: "meghalaya",
        title: "Nongriat, the long way down",
        oneLiner:
            "3,500 stone steps into a rainforest that hasn't quite been written about yet.",
        duration: "5 nights",
        durationBucket: "medium",
        budgetBand: "30-60k",
        budgetLabel: "₹30,000 – ₹60,000",
        season: "Oct–Mar",
        seasonBucket: "winter",
        mood: "Rivers · root bridges · monsoon air",
        image:
            "https://images.unsplash.com/photo-1669628182335-4c50980f419d?auto=format&fit=crop&w=1600&q=70",
        intro:
            "Most travellers stop at the famous double-decker root bridge and turn back. We don't. Beyond Nongriat is Mawsaw, then Rainbow Falls, then a third bridge almost no one photographs. You'll stay two nights at the bottom — long enough for the forest to feel ordinary.",
        days: [
            { d: "Day 01", t: "Shillong", b: "Markets, local breakfast at Police Bazaar, easy first day." },
            { d: "Day 02", t: "Tyrna → Nongriat", b: "Down 3,500 steps. Lunch at the village school." },
            { d: "Day 03", t: "Rainbow Falls", b: "A slow morning hike. Swim. Read. Talk to the village headman." },
            { d: "Day 04", t: "Up & out → Cherrapunji", b: "Reward yourself. A hot meal. A real bed." },
            { d: "Day 05", t: "Mawlynnong → Shillong", b: "Cleanest village in Asia. Stop for tea." },
            { d: "Day 06", t: "Fly out", b: "" },
        ],
        included: ["Khasi guide from Tyrna", "Homestay in Nongriat (2 nights)", "All meals", "Cherrapunji boutique stay (1 night)"],
    },

    // ───── CULTURE ─────
    {
        slug: "hornbill-week",
        traveller: "culture",
        state: "nagaland",
        title: "Ten days at Hornbill",
        oneLiner:
            "Sixteen tribes, one fire, and a week to learn the difference between a Konyak and an Ao greeting.",
        duration: "9 nights",
        durationBucket: "long",
        budgetBand: "60-120k",
        budgetLabel: "₹60,000 – ₹1,20,000",
        season: "Dec 1–10",
        seasonBucket: "winter",
        mood: "Drums · woodsmoke · firelight",
        image:
            "https://images.unsplash.com/photo-1634918221993-384376dc8c49?auto=format&fit=crop&w=1600&q=70",
        intro:
            "Hornbill is not a festival you photograph. It's a festival you sit inside of — slowly, over days, with the same people, until the morungs stop feeling like museum exhibits and start feeling like rooms with a fire and an old uncle who wants to tell you something. Ten nights gives you time to learn names.",
        days: [
            { d: "Day 01", t: "Dimapur → Kohima", b: "Settle into a heritage homestay above the city." },
            { d: "Day 02–06", t: "Kisama Heritage Village", b: "Different tribe each day. Long mornings. Slow lunches. Evening folk songs around a fire." },
            { d: "Day 07", t: "Khonoma", b: "Asia's first green village. Stay with the Angamis." },
            { d: "Day 08", t: "Touphema", b: "A model heritage village run by its own community." },
            { d: "Day 09", t: "Kohima", b: "WW2 cemetery. Quiet morning. Last dinner with the family." },
            { d: "Day 10", t: "Fly out", b: "" },
        ],
        included: ["Angami homestay (Kohima base)", "All festival passes", "Tribal-led storytelling sessions", "Local guide every day"],
    },
    {
        slug: "majuli-river",
        traveller: "culture",
        state: "assam",
        title: "Majuli, where the river decides",
        oneLiner:
            "Three days on the world's largest river island — mask-making, satras, mustard fields, mornings in mist.",
        duration: "5 nights",
        durationBucket: "medium",
        budgetBand: "30-60k",
        budgetLabel: "₹30,000 – ₹60,000",
        season: "Nov–Feb",
        seasonBucket: "winter",
        mood: "Mustard · masks · river dawn",
        image:
            "https://images.unsplash.com/photo-1602020277972-99978250c8bd?auto=format&fit=crop&w=1600&q=70",
        intro:
            "Majuli is a 16th-century Vaishnavite world floating on the Brahmaputra — slowly being eaten by the same river. Stay in a bamboo cottage at Garmur. Visit the satras at dawn when the chants begin. Watch a mask-maker work for an hour without saying a word.",
        days: [
            { d: "Day 01", t: "Jorhat", b: "Tea-bungalow lunch. Walk the gardens." },
            { d: "Day 02", t: "Ferry to Majuli", b: "An hour on a country boat. Settle into your bamboo cottage." },
            { d: "Day 03", t: "Satras & masks", b: "Auniati, Kamalabari, Samaguri. A slow afternoon by the river." },
            { d: "Day 04", t: "Mishing village", b: "Lunch with a Mishing family. Loom-weaving. River fish, smoked." },
            { d: "Day 05", t: "Back to Jorhat", b: "Ferry. Drive. Tea. Sleep." },
            { d: "Day 06", t: "Fly out", b: "" },
        ],
        included: ["Bamboo eco-cottage at Garmur", "Local boat transfers", "Satra & artisan visits", "All meals (mostly Mishing)"],
    },
    {
        slug: "loktak-resilience",
        traveller: "culture",
        state: "manipur",
        title: "Loktak, on a phumdi morning",
        oneLiner:
            "A floating lake, a classical dance form, and a state that quietly insists you understand it.",
        duration: "4 nights",
        durationBucket: "short",
        budgetBand: "30-60k",
        budgetLabel: "₹30,000 – ₹60,000",
        season: "Oct–Mar",
        seasonBucket: "winter",
        mood: "Lake · drums · weaving",
        image:
            "https://images.unsplash.com/photo-1674722612663-c34ad2c24648?auto=format&fit=crop&w=1600&q=70",
        intro:
            "Loktak is the only floating lake in the world — its phumdis (mats of vegetation) drift like a slow herd. We stay at a Meitei homestay on the lake's edge, watch a Pung Cholom drummer, and end at Kangla. Manipur asks for patience. It returns it tenfold.",
        days: [
            { d: "Day 01", t: "Imphal", b: "Ima Keithel — the all-women market that has run for 500 years." },
            { d: "Day 02", t: "Loktak", b: "Boat at sunrise. Phumdi homestay. Quiet evening." },
            { d: "Day 03", t: "Sendra & Keibul Lamjao", b: "Look for the dancing deer. Lunch by the water." },
            { d: "Day 04", t: "Kangla & Raas Leela", b: "Back in Imphal. Private classical-dance evening." },
            { d: "Day 05", t: "Fly out", b: "" },
        ],
        included: ["Phumdi homestay (1 night)", "Imphal boutique stay (3 nights)", "Private Raas Leela performance", "All transfers & meals"],
    },

    // ───── LUXURY ─────
    {
        slug: "tea-bungalow-week",
        traveller: "luxury",
        state: "assam",
        title: "A week in a tea bungalow",
        oneLiner:
            "Colonial bungalows in Dibrugarh, second-flush tea, mornings on a verandah that has not changed since 1875.",
        duration: "6 nights",
        durationBucket: "medium",
        budgetBand: "120k+",
        budgetLabel: "₹1,20,000 +",
        season: "Oct–Apr",
        seasonBucket: "winter",
        mood: "Verandahs · second flush · slow light",
        image:
            "https://images.unsplash.com/photo-1602020277972-99978250c8bd?auto=format&fit=crop&w=1600&q=70",
        intro:
            "There are perhaps eight tea bungalows in upper Assam where you can still wake to a butler bringing tea on a wicker tray, walk the gardens before breakfast, and feel — for a moment — that nothing is asked of you. We've spent fifteen years learning which ones still get it right.",
        days: [
            { d: "Day 01", t: "Dibrugarh", b: "Settle in. Long bath. Dinner cooked by the in-house cook. No restaurant noise." },
            { d: "Day 02", t: "Plantation walk", b: "First-light walk with the planter. Tea-tasting after." },
            { d: "Day 03", t: "Slow day", b: "Lawn games, library, an afternoon nap. Croquet if you must." },
            { d: "Day 04", t: "Sivasagar", b: "Ahom monuments. Late lunch in another bungalow." },
            { d: "Day 05", t: "Kaziranga (overnight)", b: "Helicopter transfer if you wish. Premium camp at Diphlu River Lodge." },
            { d: "Day 06", t: "Safari & return", b: "Dawn safari. Late lunch. Back to Dibrugarh by road." },
            { d: "Day 07", t: "Fly out", b: "Slowly." },
        ],
        included: ["Heritage tea bungalow (5 nights)", "Diphlu River Lodge (1 night)", "All transfers, optional helicopter", "Private guide & in-house cook"],
    },
    {
        slug: "tawang-quiet",
        traveller: "luxury",
        state: "arunachal",
        title: "Tawang, in a private monastery hush",
        oneLiner:
            "Glamping above the clouds, a 5 a.m. prayer call you walk to in slippers, no buses, no crowds.",
        duration: "7 nights",
        durationBucket: "medium",
        budgetBand: "120k+",
        budgetLabel: "₹1,20,000 +",
        season: "Apr–Oct",
        seasonBucket: "summer",
        mood: "Butter lamps · cold light · stillness",
        image:
            "https://images.pexels.com/photos/2815093/pexels-photo-2815093.jpeg?auto=compress&cs=tinysrgb&w=1600&q=70",
        intro:
            "Tawang is the second-largest monastery on earth. Most travellers see it in a half-day. We give it three. You'll stay in a small luxury camp ten minutes away — heated tents, copper bathtubs, a private chef — and walk to the 5 a.m. prayer in your own time, in your own silence.",
        days: [
            { d: "Day 01", t: "Guwahati → Bomdila", b: "Helicopter transfer if you prefer." },
            { d: "Day 02", t: "Bomdila → Dirang → Tawang", b: "Sela Pass. Frozen lake. Late arrival at the camp." },
            { d: "Day 03–05", t: "Tawang", b: "Monastery, Urgelling, Bumla, the war memorial. Slow dinners." },
            { d: "Day 06", t: "Dirang", b: "Apple orchard lunch. Hot springs in the evening." },
            { d: "Day 07", t: "Bomdila → Guwahati", b: "" },
            { d: "Day 08", t: "Fly out", b: "" },
        ],
        included: ["Boutique heated tents (4 nights)", "Heritage homestay Dirang (2 nights)", "Private vehicle + driver", "ILP & all permits", "Optional helicopter transfers"],
    },
    {
        slug: "sikkim-cardamom",
        traveller: "luxury",
        state: "sikkim",
        title: "Pelling, between two snow peaks",
        oneLiner:
            "A private cardamom estate in West Sikkim, Kanchenjunga from the bedroom window, butter tea at dawn.",
        duration: "5 nights",
        durationBucket: "medium",
        budgetBand: "60-120k",
        budgetLabel: "₹60,000 – ₹1,20,000",
        season: "Mar–May & Oct–Nov",
        seasonBucket: "summer",
        mood: "Cardamom · prayer flags · cold air",
        image:
            "https://images.unsplash.com/photo-1672096099708-e9e379cd63c7?auto=format&fit=crop&w=1600&q=70",
        intro:
            "There is a cardamom estate above Pelling, owned by a family we've worked with for a decade. Three rooms only. The house is quiet by design — no television, no menu, just whatever the kitchen feels like that morning. Wake at 5 for Kanchenjunga to turn gold. Sleep early.",
        days: [
            { d: "Day 01", t: "Bagdogra → Pelling", b: "Drive up. First sunset on Kanchenjunga." },
            { d: "Day 02", t: "Estate day", b: "Walk the cardamom drying houses. Lunch in the courtyard." },
            { d: "Day 03", t: "Pemayangtse & Khecheopalri", b: "Two of Sikkim's quietest monasteries. A sacred lake." },
            { d: "Day 04", t: "Yuksom", b: "First capital of Sikkim. Slow walks. Older silence." },
            { d: "Day 05", t: "Back to Pelling", b: "Final long bath, final dawn." },
            { d: "Day 06", t: "Drive to Bagdogra", b: "" },
        ],
        included: ["Private cardamom-estate house", "All meals from the family kitchen", "Driver-guide", "Permits & monastery donations"],
    },

    // ───── BACKPACKER ─────
    {
        slug: "shillong-loop",
        traveller: "backpacker",
        state: "meghalaya",
        title: "The Shillong loop, ₹12,000 in",
        oneLiner:
            "Six days, two hostels, one root bridge, and a notebook that ends up better than your phone.",
        duration: "5 nights",
        durationBucket: "medium",
        budgetBand: "under-30k",
        budgetLabel: "Under ₹30,000",
        season: "Oct–Mar",
        seasonBucket: "winter",
        mood: "Hostel chats · rainy roads · cheap chai",
        image:
            "https://images.unsplash.com/photo-1669628182335-4c50980f419d?auto=format&fit=crop&w=1600&q=70",
        intro:
            "If you have time and very little money, Meghalaya is the kindest state in the Northeast. ₹600 dorm beds, shared sumos for ₹150 a leg, lunch under ₹100. We've drawn a six-day loop that costs roughly ₹12,000 all-in if you're careful, and gives you the rainforest, the bridge, the cleanest village in Asia, and three nights in Shillong with people who play very good blues.",
        days: [
            { d: "Day 01", t: "Shillong", b: "Hostel in Laitumkhrah. Police Bazaar walk. Live music at night." },
            { d: "Day 02", t: "Cherrapunji", b: "Shared sumo. Stay at a budget homestay." },
            { d: "Day 03–04", t: "Nongriat", b: "Down to the root bridge. Two nights at ₹500 each." },
            { d: "Day 05", t: "Mawlynnong → Shillong", b: "One more sumo. Hostel bunk. Last beer." },
            { d: "Day 06", t: "Fly / bus out", b: "" },
        ],
        included: ["Hand-drawn route map", "Vetted hostel & homestay list", "WhatsApp helpline for sumo bookings", "A pre-trip call with our backpacker curator"],
    },
    {
        slug: "ziro-on-50",
        traveller: "backpacker",
        state: "arunachal",
        title: "Ziro for the music, on ₹2k a day",
        oneLiner:
            "Four nights of indie music in a rice valley most of the world hasn't heard of yet.",
        duration: "4 nights",
        durationBucket: "short",
        budgetBand: "under-30k",
        budgetLabel: "Under ₹30,000",
        season: "Late Sept",
        seasonBucket: "monsoon",
        mood: "Apatani fields · stage lights · rain",
        image:
            "https://images.unsplash.com/photo-1742489468038-b7e948b6fab1?auto=format&fit=crop&w=1600&q=70",
        intro:
            "Ziro Music Festival happens in a rice paddy. Stages built of bamboo. Bands flown in from Iceland, Tokyo, Imphal. Camp on-site for ₹500 a night. The paddies are still being harvested while the indie sets play. Few festivals on earth feel less commercial.",
        days: [
            { d: "Day 01", t: "Naharlagun → Ziro", b: "Shared sumo, 6 hours. Camp up by sunset." },
            { d: "Day 02–04", t: "Festival", b: "Sets begin afternoon. Eat at the local food stalls. Apatani villagers lend you umbrellas." },
            { d: "Day 05", t: "Down to Naharlagun", b: "Sleep on the train if you must." },
        ],
        included: ["Festival pass guidance", "Camping spot booking", "Pre-trip call with our Ziro regular", "Vetted shared-sumo contacts"],
    },
    {
        slug: "mizoram-month",
        traveller: "backpacker",
        state: "mizoram",
        title: "Aizawl slow, for a month",
        oneLiner:
            "Long stay. Cheap homestay. Sundays where the whole state goes quiet.",
        duration: "30 nights",
        durationBucket: "long",
        budgetBand: "under-30k",
        budgetLabel: "Under ₹30,000",
        season: "Oct–Mar",
        seasonBucket: "winter",
        mood: "Hills · gospel · slow Sundays",
        image:
            "https://images.unsplash.com/photo-1742489468038-b7e948b6fab1?auto=format&fit=crop&w=1600&q=70",
        intro:
            "Mizoram is the quietest state in India. A month here is not too much. ₹8,000 for a room with a view of the entire ridge, ₹100 thalis at the bazaar, free Sunday hospitality at any local church if you ask politely. This isn't a tour. It's a coordinated long-stay.",
        days: [
            { d: "Week 01", t: "Aizawl", b: "Settle. Find your café. Find your bakery. Find your barber." },
            { d: "Week 02", t: "Reiek & Hmuifang", b: "Day trips by shared taxi. ₹300 a leg." },
            { d: "Week 03", t: "Champhai", b: "The old border country. Vines, churches, slow afternoons." },
            { d: "Week 04", t: "Aizawl again", b: "Friends now. A different kind of leaving." },
        ],
        included: ["Long-stay homestay deal (30 nights)", "Local SIM & contact list", "Welcome dinner", "Mid-stay check-in call"],
    },
];

export const TRAVELLER_META = {
    adventure: {
        id: "adventure",
        number: "01",
        title: "Adventure Seeker",
        line: "Trails with no crowds. Rivers with no names.",
        long: "For travellers chasing movement, terrain, uncertainty, and stories earned the hard way.",
        focus: ["Treks", "Remote valleys", "Camping", "Bike expeditions", "Mountain roads", "Wild landscapes"],
        mood: "Mist · boots · campfires · cold mornings",
        tone: "Raw, alive, exploratory.",
        image:
            "https://images.unsplash.com/photo-1692652567309-5ac9ae7fe6f7?auto=format&fit=crop&w=1600&q=70",
    },
    culture: {
        id: "culture",
        number: "02",
        title: "Culture Lover",
        line: "Sit inside a Naga longhouse. Stay till the fire dies.",
        long: "For travellers who want to understand people, rituals, food, music, and memory.",
        focus: ["Hornbill Festival", "Majuli monasteries", "Tribal villages", "Traditional crafts", "Folk music", "Indigenous traditions"],
        mood: "Warm interiors · candles · textiles · markets",
        tone: "Human, intimate, respectful.",
        image:
            "https://images.unsplash.com/photo-1634918221993-384376dc8c49?auto=format&fit=crop&w=1600&q=70",
    },
    luxury: {
        id: "luxury",
        number: "03",
        title: "Luxury Traveller",
        line: "Every detail handled. Every silence kept.",
        long: "For travellers who want silence, comfort, curation, and unforgettable settings.",
        focus: ["Boutique stays", "Tea estate retreats", "Curated road journeys", "Wellness", "Mountain-view stays", "Slow premium travel"],
        mood: "Soft lighting · foggy balconies · warm wood",
        tone: "Elegant, calm, cinematic.",
        image:
            "https://images.unsplash.com/photo-1672096099708-e9e379cd63c7?auto=format&fit=crop&w=1600&q=70",
    },
    backpacker: {
        id: "backpacker",
        number: "04",
        title: "Backpacker",
        line: "Stretch ₹1000 across three days. Come home rich.",
        long: "For travellers stretching time instead of money.",
        focus: ["Hostel circuits", "Shared transport", "Budget homestays", "Spontaneous journeys", "Café culture", "Long stays"],
        mood: "Backpacks · rainy roads · cheap tea stalls",
        tone: "Free, youthful, spontaneous.",
        image:
            "https://images.unsplash.com/photo-1742489468038-b7e948b6fab1?auto=format&fit=crop&w=1600&q=70",
    },
};

export const FILTERS = {
    states: [
        { id: "assam", label: "Assam" },
        { id: "meghalaya", label: "Meghalaya" },
        { id: "nagaland", label: "Nagaland" },
        { id: "arunachal", label: "Arunachal Pradesh" },
        { id: "manipur", label: "Manipur" },
        { id: "mizoram", label: "Mizoram" },
        { id: "tripura", label: "Tripura" },
        { id: "sikkim", label: "Sikkim" },
    ],
    durations: [
        { id: "short", label: "Up to 5 nights" },
        { id: "medium", label: "5–8 nights" },
        { id: "long", label: "9 nights or longer" },
    ],
    budgets: [
        { id: "under-30k", label: "Under ₹30k" },
        { id: "30-60k", label: "₹30 – 60k" },
        { id: "60-120k", label: "₹60 – 1.2L" },
        { id: "120k+", label: "Above ₹1.2L" },
    ],
    seasons: [
        { id: "winter", label: "Winter (Oct–Mar)" },
        { id: "summer", label: "Summer (Apr–Jun)" },
        { id: "monsoon", label: "Monsoon (Jul–Sept)" },
    ],
};
